import { useParams, useNavigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { apiClient } from '@/services/apiClient'
import { useAuthStore } from '@/store/authStore'

interface WithdrawalDetail {
  withdrawal: {
    id: string
    user_id: string
    order_type: number
    amount: number
    fee: number
    real_amount: number
    bank_info: any
    wallet_info?: {
      id: string
      wallet_name: string
      wallet_address: string
      channel_code: string
      channel_name: string
      channel_icon: string
      account_holder_name?: string
      bank_name?: string
      bank_branch?: string
      qr_code_image?: string
      network?: string
    }
    status: string
    finance_approved_by?: string
    finance_approved_at?: string
    finance_notes?: string
    audit_approved_by?: string
    audit_approved_at?: string
    audit_notes?: string
    rejected_by?: string
    rejected_at?: string
    rejection_reason?: string
    completed_at?: string
    created_at: string
    updated_at: string
  }
  user: {
    id: string
    full_name: string
    email: string
    phone?: string
    operator_id?: string
    operator_name?: string
    current_balance: number
    bonus_balance: number
    freeze_balance: number
    max_withdrawable: number
  }
  approval_history: Array<{
    action: string
    admin_name: string
    timestamp: string
    notes?: string
    reason?: string
  }>
  related_transactions: Array<{
    id: string
    type: string
    amount: number
    description: string
    created_at: string
  }>
  commission_config?: {
    platform_commission_rate: number
    operator_commission_rate: number
  }
  operator?: {
    id: string
    name: string
    email: string
  }
}

export default function WithdrawalDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const [withdrawal, setWithdrawal] = useState<WithdrawalDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState(false)
  const [showApprovalModal, setShowApprovalModal] = useState(false)
  const [approvalAction, setApprovalAction] = useState<'finance_approve' | 'audit_approve' | 'reject'>('finance_approve')
  const [notes, setNotes] = useState('')
  const [rejectionReason, setRejectionReason] = useState('')
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [paymentProof, setPaymentProof] = useState('')

  useEffect(() => {
    if (id) {
      fetchWithdrawalDetail()
    }
  }, [id])

  const fetchWithdrawalDetail = async () => {
    try {
      setLoading(true)
      const response = await apiClient.getWithdrawalDetail(id!)
      if (response.success) {
        setWithdrawal(response.data)
      } else {
        console.error('Failed to fetch withdrawal detail:', response.message)
      }
    } catch (error) {
      console.error('Error fetching withdrawal detail:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleApprovalAction = async () => {
    if (!withdrawal) return

    try {
      setActionLoading(true)
      const response = await apiClient.approveWithdrawal({
        withdrawal_id: withdrawal.withdrawal.id,
        action: approvalAction,
        notes: approvalAction === 'reject' ? undefined : notes,
        rejection_reason: approvalAction === 'reject' ? rejectionReason : undefined
      })

      if (response.success) {
        setShowApprovalModal(false)
        setNotes('')
        setRejectionReason('')
        await fetchWithdrawalDetail() // 刷新数据
      } else {
        alert('操作失败: ' + response.message)
      }
    } catch (error) {
      console.error('Error processing approval:', error)
      alert('操作失败，请重试')
    } finally {
      setActionLoading(false)
    }
  }

  const handlePaymentAction = async () => {
    if (!withdrawal || !paymentProof.trim()) return

    try {
      setActionLoading(true)
      const response = await apiClient.processWithdrawalPayment(
        withdrawal.withdrawal.id,
        paymentProof
      )

      if (response.success) {
        setShowPaymentModal(false)
        setPaymentProof('')
        await fetchWithdrawalDetail() // 刷新数据
      } else {
        alert('打款失败: ' + response.message)
      }
    } catch (error) {
      console.error('Error processing payment:', error)
      alert('打款失败，请重试')
    } finally {
      setActionLoading(false)
    }
  }

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      pending: { label: '待财务审核', className: 'bg-yellow-100 text-yellow-800' },
      finance_approved: { label: '待审计审核', className: 'bg-blue-100 text-blue-800' },
      audit_approved: { label: '待打款', className: 'bg-purple-100 text-purple-800' },
      completed: { label: '已完成', className: 'bg-green-100 text-green-800' },
      rejected: { label: '已拒绝', className: 'bg-red-100 text-red-800' }
    }
    const config = statusConfig[status as keyof typeof statusConfig] || { label: status, className: 'bg-gray-100 text-gray-800' }
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.className}`}>
        {config.label}
      </span>
    )
  }

  const getActionLabel = (action: string) => {
    const actionLabels = {
      finance_approved: '财务审批通过',
      audit_approved: '审计审批通过',
      rejected: '拒绝提现',
      completed: '提现完成'
    }
    return actionLabels[action as keyof typeof actionLabels] || action
  }

  const canPerformAction = (action: 'finance_approve' | 'audit_approve' | 'reject') => {
    if (!withdrawal || !user) return false

    const status = withdrawal.withdrawal.status
    const userRole = user.role
    const userPermissions = user.permissions || []

    // 检查状态是否允许操作
    const validTransitions: Record<string, string[]> = {
      pending: ['finance_approve', 'reject'],
      finance_approved: ['audit_approve', 'reject'],
      audit_approved: [],
      completed: [],
      rejected: []
    }

    if (!validTransitions[status]?.includes(action)) {
      return false
    }

    // 检查权限
    const requiredRoles = {
      finance_approve: ['admin', 'finance'],
      audit_approve: ['admin', 'audit'],
      reject: ['admin', 'finance', 'audit']
    }

    const requiredPermissions = {
      finance_approve: 'withdrawals:approve',
      audit_approve: 'withdrawals:approve',
      reject: 'withdrawals:reject'
    }

    const hasRole = requiredRoles[action].includes(userRole)
    const hasPermission = userPermissions.includes(requiredPermissions[action]) || userRole === 'admin'

    return hasRole && hasPermission
  }

  if (loading) {
    return (
      <div className="page-content">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">加载中...</div>
        </div>
      </div>
    )
  }

  if (!withdrawal) {
    return (
      <div className="page-content">
        <div className="text-center py-12">
          <div className="text-gray-500 mb-4">提现订单不存在</div>
          <button
            onClick={() => navigate('/withdrawals')}
            className="btn btn-primary"
          >
            返回提现列表
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="page-content">
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="page-title">提现详情</h1>
            <p className="page-subtitle">提现ID: {withdrawal.withdrawal.id}</p>
          </div>
          <div className="flex items-center space-x-4">
            {getStatusBadge(withdrawal.withdrawal.status)}
            <button
              onClick={() => navigate('/withdrawals')}
              className="btn btn-secondary"
            >
              返回列表
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 提现信息 */}
        <div className="lg:col-span-2 space-y-6">
          <div className="card">
            <div className="card-header flex items-center justify-between">
              <h3 className="card-title">提现信息</h3>
              {getStatusBadge(withdrawal.withdrawal.status)}
            </div>
            <div className="card-body">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">提现金额</label>
                  <div className="text-lg font-semibold text-gray-900">¥{withdrawal.withdrawal.amount.toLocaleString()}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">手续费</label>
                  <div className="text-lg font-semibold text-red-600">¥{withdrawal.withdrawal.fee.toLocaleString()}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">实际到账</label>
                  <div className="text-lg font-semibold text-green-600">¥{withdrawal.withdrawal.real_amount.toLocaleString()}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">申请时间</label>
                  <div className="text-sm text-gray-900">{new Date(withdrawal.withdrawal.created_at).toLocaleString()}</div>
                </div>
              </div>

              {withdrawal.withdrawal.wallet_info ? (
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {withdrawal.withdrawal.wallet_info.channel_icon} {withdrawal.withdrawal.wallet_info.channel_name} 钱包信息
                  </label>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">钱包名称:</span>
                        <span className="ml-2 font-medium">{withdrawal.withdrawal.wallet_info.wallet_name}</span>
                      </div>
                      
                      {withdrawal.withdrawal.wallet_info.channel_code === 'bank_transfer' && (
                        <>
                          <div>
                            <span className="text-gray-500">账户名:</span>
                            <span className="ml-2 font-medium">{withdrawal.withdrawal.wallet_info.account_holder_name}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">银行名称:</span>
                            <span className="ml-2 font-medium">{withdrawal.withdrawal.wallet_info.bank_name}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">银行卡号:</span>
                            <span className="ml-2 font-medium font-mono">{withdrawal.withdrawal.wallet_info.wallet_address}</span>
                          </div>
                          {withdrawal.withdrawal.wallet_info.bank_branch && (
                            <div>
                              <span className="text-gray-500">开户行:</span>
                              <span className="ml-2 font-medium">{withdrawal.withdrawal.wallet_info.bank_branch}</span>
                            </div>
                          )}
                        </>
                      )}
                      
                      {withdrawal.withdrawal.wallet_info.channel_code === 'okx' && (
                        <>
                          <div className="col-span-2">
                            <span className="text-gray-500">钱包地址:</span>
                            <span className="ml-2 font-medium font-mono break-all">{withdrawal.withdrawal.wallet_info.wallet_address}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">网络类型:</span>
                            <span className="ml-2 font-medium text-orange-600">{withdrawal.withdrawal.wallet_info.network}</span>
                          </div>
                          <div className="col-span-2 mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
                            ⚠️ 请确认网络类型正确，否则资金将无法到账
                          </div>
                        </>
                      )}
                      
                      {(withdrawal.withdrawal.wallet_info.channel_code === 'alipay' || withdrawal.withdrawal.wallet_info.channel_code === 'wechat') && (
                        <>
                          <div>
                            <span className="text-gray-500">账户:</span>
                            <span className="ml-2 font-medium">{withdrawal.withdrawal.wallet_info.wallet_address}</span>
                          </div>
                          {withdrawal.withdrawal.wallet_info.qr_code_image && (
                            <div className="col-span-2">
                              <span className="text-gray-500">收款码:</span>
                              <div className="mt-2">
                                <img 
                                  src={withdrawal.withdrawal.wallet_info.qr_code_image} 
                                  alt="收款码" 
                                  className="max-w-xs border border-gray-300 rounded"
                                />
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ) : withdrawal.withdrawal.bank_info && (
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">银行信息（旧系统）</label>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">银行名称:</span>
                        <span className="ml-2 font-medium">{withdrawal.withdrawal.bank_info.bank_name}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">账户名:</span>
                        <span className="ml-2 font-medium">{withdrawal.withdrawal.bank_info.account_name}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">银行卡号:</span>
                        <span className="ml-2 font-medium">{withdrawal.withdrawal.bank_info.account_number}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">开户行:</span>
                        <span className="ml-2 font-medium">{withdrawal.withdrawal.bank_info.branch_name}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* 手续费分配 */}
              {withdrawal.commission_config && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="text-xs text-gray-600 space-y-1">
                    <div>
                      运营商分配: ¥{(withdrawal.withdrawal.fee * withdrawal.commission_config.operator_commission_rate).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ({(withdrawal.commission_config.operator_commission_rate * 100).toFixed(0)}%)
                      {withdrawal.operator && <span className="ml-2 text-gray-500">- {withdrawal.operator.name} ({withdrawal.operator.email})</span>}
                    </div>
                    <div>平台分配: ¥{(withdrawal.withdrawal.fee * withdrawal.commission_config.platform_commission_rate).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ({(withdrawal.commission_config.platform_commission_rate * 100).toFixed(0)}%)</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* 审批历史 */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">审批历史</h3>
            </div>
            <div className="card-body">
              {withdrawal.approval_history.length > 0 ? (
                <div className="space-y-4">
                  {withdrawal.approval_history.map((history, index) => (
                    <div key={index} className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg">
                      <div className="flex-shrink-0">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${
                          history.action === 'rejected' ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'
                        }`}>
                          {index + 1}
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <div className="font-medium text-gray-900">{getActionLabel(history.action)}</div>
                          <div className="text-sm text-gray-500">{new Date(history.timestamp).toLocaleString()}</div>
                        </div>
                        <div className="text-sm text-gray-600 mt-1">操作人: {history.admin_name}</div>
                        {history.notes && (
                          <div className="text-sm text-gray-600 mt-2">备注: {history.notes}</div>
                        )}
                        {history.reason && (
                          <div className="text-sm text-red-600 mt-2">拒绝原因: {history.reason}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">暂无审批记录</div>
              )}
            </div>
          </div>

          {/* 相关交易 */}
          {withdrawal.related_transactions.length > 0 && (
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">相关交易记录</h3>
              </div>
              <div className="card-body">
                <div className="space-y-3">
                  {withdrawal.related_transactions.map((tx) => (
                    <div key={tx.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <div className="font-medium text-gray-900">{tx.description}</div>
                        <div className="text-sm text-gray-500">{new Date(tx.created_at).toLocaleString()}</div>
                      </div>
                      <div className={`font-semibold ${tx.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {tx.amount >= 0 ? '+' : ''}¥{tx.amount.toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 用户信息和操作 */}
        <div className="space-y-6">
          {/* 用户信息 */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">{withdrawal.withdrawal.order_type === 2 ? '运营商信息' : '用户信息'}</h3>
            </div>
            <div className="card-body">
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">姓名</label>
                  <div className="text-sm text-gray-900">{withdrawal.user.full_name}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">邮箱</label>
                  <div className="text-sm text-gray-900">{withdrawal.user.email}</div>
                </div>
                {withdrawal.user.phone && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">手机号</label>
                    <div className="text-sm text-gray-900">{withdrawal.user.phone}</div>
                  </div>
                )}
                {withdrawal.user.operator_id && withdrawal.withdrawal.order_type === 1 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">运营商</label>
                    <div className="text-sm text-gray-900">
                      {withdrawal.user.operator_name} ({withdrawal.user.operator_id})
                    </div>
                  </div>
                )}
                <div className="pt-3 border-t border-gray-200">
                  <label className="block text-sm font-medium text-gray-700 mb-2">账户余额</label>
                  <div className="space-y-2 text-sm">
                    {withdrawal.withdrawal.order_type === 2 ? (
                      // 运营商提现：只显示佣金余额
                      <>
                       <div className="flex justify-between">
                          <span className="text-gray-600">余额:</span>
                          <span className="font-semibold text-green-600">¥{withdrawal.user.current_balance.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">佣金总额:</span>
                          <span className="font-semibold text-green-600">¥{withdrawal.user.bonus_balance.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">冻结金额:</span>
                          <span className="font-semibold text-orange-600">¥{withdrawal.user.freeze_balance.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </div>
                        <div className="flex justify-between pt-2 border-t border-gray-200">
                          <span className="text-gray-700 font-medium">最大可提现:</span>
                          <span className="font-bold text-green-700">¥{withdrawal.user.max_withdrawable.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </div>
                        <div className="mt-2 text-xs text-gray-500">
                          <p>* 运营商提现免手续费</p>
                          <p>* 最大可提现 = 佣金余额 - 冻结金额</p>
                        </div>
                      </>
                    ) : (
                      // 用户提现：显示真实余额、赠金余额等
                      <>
                        <div className="flex justify-between">
                          <span className="text-gray-600">真实余额:</span>
                          <span className="font-semibold text-green-600">¥{withdrawal.user.current_balance.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">赠金余额:</span>
                          <span className="font-semibold text-blue-600">¥{withdrawal.user.bonus_balance.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">冻结金额:</span>
                          <span className="font-semibold text-orange-600">¥{withdrawal.user.freeze_balance.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </div>
                        <div className="flex justify-between pt-2 border-t border-gray-200">
                          <span className="text-gray-700 font-medium">最大可提现:</span>
                          <span className="font-bold text-green-700">¥{withdrawal.user.max_withdrawable.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </div>
                        <div className="mt-2 text-xs text-gray-500">
                          <p>* 赠金不能提现</p>
                          <p>* 最大可提现 = 真实余额 - 冻结金额</p>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 操作按钮 */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">审批操作</h3>
            </div>
            <div className="card-body">
              <div className="space-y-3">
                {canPerformAction('finance_approve') && (
                  <button
                    onClick={() => {
                      setApprovalAction('finance_approve')
                      setShowApprovalModal(true)
                    }}
                    className="w-full btn btn-primary"
                  >
                    财务审批通过
                  </button>
                )}
                {canPerformAction('audit_approve') && (
                  <button
                    onClick={() => {
                      setApprovalAction('audit_approve')
                      setShowApprovalModal(true)
                    }}
                    className="w-full btn btn-success"
                  >
                    审计审批通过
                  </button>
                )}
                {withdrawal.withdrawal.status === 'audit_approved' && (
                  <button
                    onClick={() => setShowPaymentModal(true)}
                    className="w-full btn btn-success"
                  >
                    打款
                  </button>
                )}
                {canPerformAction('reject') && (
                  <button
                    onClick={() => {
                      setApprovalAction('reject')
                      setShowApprovalModal(true)
                    }}
                    className="w-full btn btn-danger"
                  >
                    拒绝提现
                  </button>
                )}
                {!canPerformAction('finance_approve') && !canPerformAction('audit_approve') && !canPerformAction('reject') && withdrawal.withdrawal.status !== 'audit_approved' && (
                  <div className="text-center py-4 text-gray-500">
                    {withdrawal.withdrawal.status === 'completed' ? '提现已完成' :
                     withdrawal.withdrawal.status === 'rejected' ? '提现已拒绝' : '无可执行操作'}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 审批操作模态框 */}
      {showApprovalModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {approvalAction === 'reject' ? '拒绝提现' : 
                 approvalAction === 'finance_approve' ? '财务审批' : '审计审批'}
              </h3>
              
              {approvalAction === 'reject' ? (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">拒绝原因 *</label>
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                    placeholder="请输入拒绝原因..."
                    required
                  />
                </div>
              ) : (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">审批备注</label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                    placeholder="请输入审批备注..."
                  />
                </div>
              )}

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowApprovalModal(false)
                    setNotes('')
                    setRejectionReason('')
                  }}
                  className="btn btn-secondary"
                  disabled={actionLoading}
                >
                  取消
                </button>
                <button
                  onClick={handleApprovalAction}
                  className={`btn ${approvalAction === 'reject' ? 'btn-danger' : 'btn-primary'}`}
                  disabled={actionLoading || (approvalAction === 'reject' && !rejectionReason.trim())}
                >
                  {actionLoading ? '处理中...' : '确认'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 打款模态框 */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">打款凭证</h3>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">打款凭证 *</label>
                <textarea
                  value={paymentProof}
                  onChange={(e) => setPaymentProof(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={4}
                  placeholder="请输入打款凭证信息（如转账单号、截图URL等）..."
                  required
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowPaymentModal(false)
                    setPaymentProof('')
                  }}
                  className="btn btn-secondary"
                  disabled={actionLoading}
                >
                  取消
                </button>
                <button
                  onClick={handlePaymentAction}
                  className="btn btn-success"
                  disabled={actionLoading || !paymentProof.trim()}
                >
                  {actionLoading ? '处理中...' : '确认打款'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}